import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { API_URL } from '../utils/config';
import { 
  FileText, Download, Eye, Upload, Search, Calendar, Building2,
  CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Filter,
  BarChart3, ArrowUpDown, ArrowUp, ArrowDown, User, Shield,
  ChevronLeft, ChevronRight
} from 'lucide-react';


const AlloggiatiReceiptsManager = () => {
  const token = localStorage.getItem('hw_token');
  const [receipts, setReceipts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSentBy, setFilterSentBy] = useState('all');
  
  // Sorting
  const [sortField, setSortField] = useState('receipt_date');
  const [sortDir, setSortDir] = useState('desc');
  
  // Pagination
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadBookingId, setUploadBookingId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingReceipts, setDownloadingReceipts] = useState(false);
  const [downloadingAllMissing, setDownloadingAllMissing] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 6}, (_, i) => currentYear - i);

  useEffect(() => {
    fetchReceipts();
    fetchStats();
  }, [filterYear, filterDateFrom, filterDateTo, filterSentBy]);

  const fetchReceipts = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_URL}/api/alloggiati/receipts?`;
      if (filterYear && filterYear !== 'all') url += `year=${filterYear}&`;
      if (filterDateFrom) url += `date_from=${filterDateFrom}&`;
      if (filterDateTo) url += `date_to=${filterDateTo}&`;
      if (filterSentBy && filterSentBy !== 'all') url += `sent_by=${filterSentBy}&`;
      if (filterSearch) url += `search=${encodeURIComponent(filterSearch)}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReceipts(data.receipts || []);
      } else {
        const err = await response.json();
        setError(err.detail || 'Errore caricamento ricevute');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/alloggiati/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  };

  const handleDownload = async (receipt) => {
    try {
      const url = `${API_URL}/api/alloggiati/receipts/${receipt.property_code}/${receipt.year}/${receipt.filename}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        window.open(data.download_url, '_blank');
      } else {
        alert('Errore download ricevuta');
      }
    } catch (e) {
      alert('Errore di connessione');
    }
  };

  const handleUpload = async () => {
    if (!uploadBookingId || !uploadFile) {
      alert('Inserisci ID prenotazione e seleziona un file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const response = await fetch(`${API_URL}/api/alloggiati/receipts/upload/${uploadBookingId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        alert('Ricevuta caricata con successo!');
        setShowUploadDialog(false);
        setUploadBookingId('');
        setUploadFile(null);
        fetchReceipts();
        fetchStats();
      } else {
        const err = await response.json();
        alert(err.detail || 'Errore caricamento');
      }
    } catch (e) {
      alert('Errore di connessione');
    }
    setUploading(false);
  };

  const handleDownloadPendingReceipts = async () => {
    if (!window.confirm('Vuoi scaricare le ricevute in sospeso dalla Questura?')) return;
    setDownloadingReceipts(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/alloggiati/receipts/download-pending`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.downloaded > 0 
          ? `Scaricate ${data.downloaded} ricevute!` 
          : `${data.message || 'Nessuna ricevuta da scaricare'}`);
        fetchReceipts();
        fetchStats();
      } else {
        setError(data.detail || 'Errore');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setDownloadingReceipts(false);
  };

  const handleDownloadAllMissing = async () => {
    if (!window.confirm('Scaricare TUTTE le ricevute mancanti? Potrebbe richiedere alcuni minuti.')) return;
    setDownloadingAllMissing(true);
    try {
      const response = await fetch(`${API_URL}/api/alloggiati/receipts/download-all-missing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.downloaded > 0
          ? `Scaricate ${data.downloaded} ricevute!`
          : `${data.message || 'Nessuna ricevuta mancante'}`);
        fetchReceipts();
        fetchStats();
      } else {
        setError(data.detail || 'Errore');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setDownloadingAllMissing(false);
  };

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-300" />;
    return sortDir === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-blue-600" />;
  };

  // Filtered + sorted data
  const processedReceipts = useMemo(() => {
    let data = [...receipts];

    // Client-side text search
    if (filterSearch) {
      const term = filterSearch.toLowerCase();
      data = data.filter(r =>
        (r.property_code || '').toLowerCase().includes(term) ||
        (r.property_name || '').toLowerCase().includes(term) ||
        (r.guest_name || '').toLowerCase().includes(term) ||
        (r.responsible_name || '').toLowerCase().includes(term) ||
        (r.booking_code || '').toLowerCase().includes(term) ||
        (r.booking_id || '').toLowerCase().includes(term)
      );
    }

    // Sort
    data.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [receipts, filterSearch, sortField, sortDir]);

  // Pagination
  const totalPages = Math.ceil(processedReceipts.length / PAGE_SIZE);
  const pagedReceipts = processedReceipts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const getStatusIcon = (status, receipt) => {
    if ((!status || status === '') && receipt?.archived) return <CheckCircle className="h-4 w-4 text-green-500" />;
    switch (status) {
      case 'confirmed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'error': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status, receipt) => {
    if ((!status || status === '') && receipt?.archived) return 'Confermato';
    switch (status) {
      case 'confirmed': return 'Confermato';
      case 'pending': return 'In attesa';
      case 'error': return 'Errore';
      default: return '-';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const resetFilters = () => {
    setFilterSearch('');
    setFilterYear(currentYear.toString());
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSentBy('all');
    setPage(0);
  };

  return (
    <div className="space-y-6" data-testid="alloggiati-receipts-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800" data-testid="receipts-title">Ricevute Questura</h2>
          <p className="text-slate-500 text-sm">Archivio ricevute AlloggiatiWeb - Conservazione 5 anni</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchReceipts(); fetchStats(); }} data-testid="refresh-receipts">
            <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPendingReceipts} disabled={downloadingReceipts || downloadingAllMissing} data-testid="download-pending-receipts">
            {downloadingReceipts ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Scarica di Ieri
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadAllMissing} disabled={downloadingReceipts || downloadingAllMissing} data-testid="download-all-missing-receipts">
            {downloadingAllMissing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
            Tutte Mancanti
          </Button>
          <Button size="sm" onClick={() => setShowUploadDialog(true)} data-testid="upload-receipt-btn">
            <Upload className="h-4 w-4 mr-1" /> Carica
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Totale Invii</p>
                  <p className="text-2xl font-bold text-slate-800">{stats.total_submissions}</p>
                </div>
                <BarChart3 className="h-6 w-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Confermati</p>
                  <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
                </div>
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">In Attesa</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Quest'anno</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.receipts_this_year}</p>
                </div>
                <Calendar className="h-6 w-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2 relative">
              <Label className="text-xs">Cerca (immobile, ospite, responsabile)</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Cerca..."
                  value={filterSearch}
                  onChange={e => { setFilterSearch(e.target.value); setPage(0); }}
                  onKeyDown={e => e.key === 'Enter' && fetchReceipts()}
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Anno</Label>
              <Select value={filterYear || "all"} onValueChange={v => { setFilterYear(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger data-testid="filter-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dal</Label>
              <Input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(0); }} data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs">Al</Label>
              <Input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(0); }} data-testid="filter-date-to" />
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Inviato da</Label>
                <Select value={filterSentBy} onValueChange={v => { setFilterSentBy(v); setPage(0); }}>
                  <SelectTrigger data-testid="filter-sent-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="responsabile">Responsabile</SelectItem>
                    <SelectItem value="sistema">Sistema (auto)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={resetFilters} className="mb-0.5" data-testid="reset-filters">
                <Filter className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>
      )}

      {/* Receipts Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            Ricevute ({processedReceipts.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Caricamento...
            </div>
          ) : processedReceipts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Nessuna ricevuta trovata per i filtri selezionati
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="receipts-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {[
                      { key: 'property_code', label: 'Immobile' },
                      { key: 'guest_name', label: 'Ospite' },
                      { key: 'booking_code', label: 'Prenotazione' },
                      { key: 'receipt_number', label: 'N. Ricevuta' },
                      { key: 'questura_sent_at', label: 'Data Invio' },
                      { key: 'downloaded_at', label: 'Data Download' },
                      { key: 'sent_by', label: 'Inviato da' },
                      { key: 'status', label: 'Stato' },
                    ].map(col => (
                      <th
                        key={col.key}
                        className="text-left p-3 text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none"
                        onClick={() => handleSort(col.key)}
                        data-testid={`sort-${col.key}`}
                      >
                        <span className="inline-flex items-center">
                          {col.label}
                          <SortIcon field={col.key} />
                        </span>
                      </th>
                    ))}
                    <th className="text-right p-3 text-xs font-medium text-slate-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pagedReceipts.map((receipt, idx) => (
                    <tr key={receipt.booking_id || idx} className="hover:bg-slate-50 transition-colors" data-testid={`receipt-row-${idx}`}>
                      {/* Immobile */}
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                          <div>
                            <p className="font-medium text-sm">{receipt.property_code}</p>
                            {receipt.property_name && (
                              <p className="text-xs text-slate-500 max-w-[150px] truncate" title={receipt.property_name}>
                                {receipt.property_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Ospite */}
                      <td className="p-3">
                        <p className="text-sm">{receipt.guest_name || '-'}</p>
                        {receipt.check_in && (
                          <p className="text-xs text-slate-400">
                            {formatDate(receipt.check_in)} - {formatDate(receipt.check_out)}
                          </p>
                        )}
                      </td>
                      {/* Prenotazione */}
                      <td className="p-3 font-mono text-sm text-slate-600">
                        {receipt.booking_code || (receipt.booking_id ? receipt.booking_id.slice(0, 8) : '-')}
                      </td>
                      {/* N. Ricevuta */}
                      <td className="p-3 text-sm">{receipt.receipt_number || receipt.filename?.replace('.pdf','').slice(-8) || '-'}</td>
                      {/* Data Invio */}
                      <td className="p-3 text-sm text-slate-600">
                        {formatDateTime(receipt.questura_sent_at)}
                      </td>
                      {/* Data Download */}
                      <td className="p-3 text-sm text-slate-600">
                        {formatDateTime(receipt.downloaded_at)}
                      </td>
                      {/* Inviato da */}
                      <td className="p-3">
                        {receipt.sent_by?.toLowerCase().includes('sistema') ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            {receipt.sent_by}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-xs">
                            <User className="h-3 w-3 mr-1" />
                            {receipt.sent_by || receipt.responsible_name || '-'}
                          </Badge>
                        )}
                      </td>
                      {/* Stato */}
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {getStatusIcon(receipt.status, receipt)}
                          <span className="text-sm">{getStatusLabel(receipt.status, receipt)}</span>
                        </div>
                      </td>
                      {/* Azioni */}
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleDownload(receipt)} title="Scarica PDF" data-testid={`download-receipt-${idx}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDownload(receipt)} title="Visualizza PDF">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {processedReceipts.length} ricevute | Pagina {page + 1} di {totalPages}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-medium text-blue-800 text-sm">Policy di Conservazione</h4>
                <p className="text-xs text-blue-700 mt-1">
                  Le ricevute vengono conservate per <strong>5 anni e 10 giorni</strong> come richiesto dalla normativa.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-slate-600 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-medium text-slate-800 text-sm">Come funziona</h4>
                <p className="text-xs text-slate-600 mt-1">
                  Le ricevute sono disponibili il giorno <strong>DOPO</strong> l'invio.
                  Vengono archiviate su Google Drive in: <code className="bg-slate-200 px-1 rounded text-xs">AlloggiatiWeb / [codice] / [anno]</code>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica Ricevuta Manuale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>ID Prenotazione *</Label>
              <Input value={uploadBookingId} onChange={e => setUploadBookingId(e.target.value)} placeholder="ID prenotazione" data-testid="upload-booking-id" />
            </div>
            <div>
              <Label>File PDF Ricevuta *</Label>
              <Input type="file" accept=".pdf" onChange={e => setUploadFile(e.target.files[0])} data-testid="upload-file-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Annulla</Button>
              <Button onClick={handleUpload} disabled={uploading}>{uploading ? 'Caricamento...' : 'Carica'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlloggiatiReceiptsManager;
